import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { ConfirmProvider } from "../../contexts/ConfirmContext";
import { ThemeProvider, useTheme } from "../../contexts/ThemeContext";
import { ToastProvider } from "../../contexts/ToastContext";
import { clearToken } from "../../lib/auth";
import { hasMinRole, useAuthMe } from "../../lib/hooks/useAuthMe";
import { useInactivityLogout } from "../../lib/hooks/useInactivityLogout";
import { PasswordModal } from "./PasswordModal";

import "../../styles/dashboard.css";

interface NavEntry {
  to: string;
  label: string;
  minRole?: "viewer" | "editor" | "admin" | "owner";
}

const NAV_ITEMS: NavEntry[] = [
  { to: "screens", label: "Pi Screens" },
  { to: "groups", label: "Screen Groups" },
  { to: "media", label: "Media" },
  { to: "advertisers", label: "Advertisers" },
  { to: "playlists", label: "Playlists" },
  { to: "reports", label: "Reports" },
  { to: "downloads", label: "Downloads" },
  { to: "settings", label: "Settings", minRole: "admin" },
];

function Shell() {
  const { theme } = useTheme();

  return (
    <div className="dashboard-app" data-theme={theme}>
      {/* Toast/Confirm render fixed-position markup as siblings of the page
          content. They must live INSIDE .dashboard-app: every .toast-* and
          .modal-* rule in dashboard.css is scoped under it, as are the theme
          variables. Outside, they render as unstyled text at the page bottom. */}
      <ToastProvider>
        <ConfirmProvider>
          <Chrome />
        </ConfirmProvider>
      </ToastProvider>
    </div>
  );
}

function Chrome() {
  const { theme, toggle } = useTheme();
  const { data: user, isLoading } = useAuthMe();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  useInactivityLogout();

  function logout() {
    clearToken();
    navigate("/login");
  }

  return (
    <>
      <nav className="sidebar">
        <div className="sidebar-logo">
          SEI<span>LAN</span>
        </div>
        {NAV_ITEMS.filter((n) => !n.minRole || hasMinRole(user, n.minRole)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
          >
            {n.label}
          </NavLink>
        ))}
        <div className="user-section">
          <button type="button" className="theme-toggle" onClick={toggle}>
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <div className="user-info">
            <strong>{isLoading ? "…" : user?.name ?? "—"}</strong>
            <span>{isLoading ? "" : user?.email ?? ""}</span>
          </div>
          <div className="user-btns">
            <button type="button" className="btn-sm-action" onClick={() => setPwOpen(true)}>
              Password
            </button>
            <button
              type="button"
              className="btn-sm-action"
              style={{ borderColor: "var(--red)", color: "var(--red)" }}
              onClick={logout}
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>
      <div className="main">
        <Outlet context={{ user }} />
      </div>
      <PasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}

export function DashboardLayout() {
  return (
    <ThemeProvider>
      <Shell />
    </ThemeProvider>
  );
}
